import { cva } from "class-variance-authority";
import React from "react";

import { getButtonIconSize, getIconColor } from "../ButtonStandard/utils";

import { ButtonStandard, ButtonStandardProps } from "../ButtonStandard";
import { Spinner } from "../Spinner";

/**
 * Props for the ButtonLoading component
 * Extends ButtonStandardProps with loading-specific functionality
 */
export type ButtonLoadingProps = ButtonStandardProps & {
	/**
	 * Controls the loading state of the button
	 * When true, shows a spinner or custom loading text and hides icons
	 */
	loading: boolean;
	/**
	 * Custom text to display during loading state
	 * If not provided, defaults to a spinner icon
	 */
	loadingText?: React.ReactNode;
};

const loadingContentWrapperVariants = cva("overflow-visible", {
	variants: {
		size: {
			small: "max-h-4",
			medium: "max-h-4",
			large: "",
		},
	},
});

/**
 * ButtonLoading Component
 *
 * A specialized button component that extends ButtonStandard to provide loading states
 * for asynchronous operations. When loading is true, it automatically:
 * - Replaces button content with a spinner or custom loading text
 * - Hides left and right icons to prevent visual clutter
 * - Maintains all other ButtonStandard functionality
 */
const ButtonLoading = (props: ButtonLoadingProps) => {
	const { loading, loadingText, iconLeft, iconRight, children, ...buttonProps } = props;

	// Create loading content - either custom text or spinner
	const loadingChildren = loadingText || (
		<div className={loadingContentWrapperVariants({ size: buttonProps.size })}>
			<Spinner
				dangerouslySetClassName={"p-0"}
				color={getIconColor(buttonProps.variant, buttonProps.color)}
				size={getButtonIconSize(buttonProps.size || "medium")}
			/>
		</div>
	);

	return (
		<ButtonStandard
			iconLeft={loading ? undefined : iconLeft}
			iconRight={loading ? undefined : iconRight}
			{...buttonProps}
		>
			{loading ? loadingChildren : children}
		</ButtonStandard>
	);
};

ButtonLoading.displayName = "ButtonLoading";

export { ButtonLoading };
